import { FunctionsHttpError } from '@supabase/supabase-js';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { must, useDb } from './db';

export type VideoRole = 'teacher' | 'student' | 'observer';
export type VideoError =
  | 'not_allowed'
  | 'not_online'
  | 'not_today'
  | 'locked'
  | 'online_lesson'
  | 'invalid_session'
  | 'server_error';

export interface VideoTicket {
  url: string;
  token: string;
  role: VideoRole;
  title: string;
  endsAt: string;
  /** lessons only: for the screen-time norm of the class */
  screen?: { grade: number; lessonOfDay: number };
  /** the teacher's: the room's state when the pass was made */
  moderation?: { locked: boolean; micPolicy: 'open' | 'permission' };
}

/**
 * A lesson of the timetable, a booked meeting with a parent, or «Дошка в класі» — the board of an
 * ordinary lesson (data only), where `device` names this tab or phone: the teacher may be at the
 * projector and on the phone at once.
 */
export type VideoTarget =
  { lessonId: string } | { meetingId: string } | { boardLessonId: string; device?: string };

/** A short random name of this tab or phone for the class board (4–12 of a–z, 0–9). */
export const newDeviceId = () =>
  Array.from(
    { length: 8 },
    () => '0123456789abcdefghijklmnopqrstuvwxyz'[Math.floor(Math.random() * 36)],
  ).join('');

/**
 * «Дошка в класі» opens for an ordinary lesson (in the classroom or in the shelter) on its day,
 * from a little before it starts until it ends.
 */
export function canOpenClassBoard(
  lesson: { format: string; status: string; date: string; startsAt: string; endsAt: string },
  today: string,
  now = Date.now(),
) {
  return (
    lesson.format !== 'online' &&
    lesson.status === 'scheduled' &&
    lesson.date === today &&
    Date.parse(lesson.startsAt) - 15 * 60_000 <= now &&
    now < Date.parse(lesson.endsAt)
  );
}

/** A pass into the video room of a lesson or a meeting, from the Edge Function `video-token`. */
export function useVideoTicket(target: VideoTarget) {
  const db = useDb();
  return useQuery({
    queryKey: ['video-ticket', target],
    staleTime: 60 * 60_000,
    retry: false,
    queryFn: async (): Promise<
      { ok: true; ticket: VideoTicket } | { ok: false; error: VideoError }
    > => {
      const { data, error } = await db.functions.invoke('video-token', { body: target });
      if (!error) return { ok: true, ticket: data as VideoTicket };
      if (error instanceof FunctionsHttpError) {
        const body = (await (error.context as Response).json().catch(() => ({}))) as {
          error?: VideoError;
        };
        return { ok: false, error: body.error ?? 'server_error' };
      }
      return { ok: false, error: 'server_error' };
    },
  });
}

/** A lesson can be entered from the schedule on its day, if it is online and not cancelled. */
export function canJoinLesson(
  lesson: { format: string; status: string; date: string },
  today: string,
) {
  return lesson.format === 'online' && lesson.status === 'scheduled' && lesson.date === today;
}

/** An online lesson that is going on right now. */
export function isLessonLive(
  lesson: { format: string; status: string; startsAt: string; endsAt: string },
  now = Date.now(),
) {
  return (
    lesson.format === 'online' &&
    lesson.status === 'scheduled' &&
    Date.parse(lesson.startsAt) <= now &&
    now < Date.parse(lesson.endsAt)
  );
}

export interface PresenceVisit {
  memberId: string;
  joinedAt: string;
  leftAt: string | null;
}

export interface MemberPresence {
  /** in the video room right now */
  online: boolean;
  firstJoinedAt: string;
  /** when the member last left; null while in the room */
  lastLeftAt: string | null;
  /** minutes in the room, all visits together */
  minutes: number;
}

/** Visits (a reconnect is a new one) folded into one line per member. */
export function presenceByMember(
  visits: readonly PresenceVisit[],
  now = Date.now(),
): Map<string, MemberPresence> {
  const result = new Map<string, MemberPresence>();
  for (const v of [...visits].sort((a, b) => a.joinedAt.localeCompare(b.joinedAt))) {
    const until = v.leftAt ? Date.parse(v.leftAt) : now;
    const minutes = Math.max(0, (until - Date.parse(v.joinedAt)) / 60_000);
    const known = result.get(v.memberId);
    result.set(v.memberId, {
      online: v.leftAt === null || (known?.online ?? false),
      firstJoinedAt: known?.firstJoinedAt ?? v.joinedAt,
      lastLeftAt: v.leftAt === null || known?.online ? null : v.leftAt,
      minutes: Math.round((known?.minutes ?? 0) + minutes),
    });
  }
  return result;
}

/**
 * Who has been in a lesson's video room, from LiveKit's webhooks. The teacher and the
 * administration see everyone; a family sees its child. Live views refresh every 10 s.
 */
export function useLessonPresence(lessonId: string | undefined, live = false) {
  const db = useDb();
  return useQuery({
    queryKey: ['lesson-presence', lessonId],
    enabled: lessonId !== undefined,
    refetchInterval: live ? 10_000 : false,
    queryFn: async () => {
      const rows = must(
        await db
          .from('lesson_presence')
          .select('member_id, joined_at, left_at')
          .eq('lesson_id', lessonId as string),
      );
      return presenceByMember(
        rows.map((r) => ({ memberId: r.member_id, joinedAt: r.joined_at, leftAt: r.left_at })),
      );
    },
  });
}

/** The teacher marks absent the students who never joined the online lesson. */
export function useMarkAbsentByPresence() {
  const db = useDb();
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (lessonId: string) =>
      must(await db.rpc('mark_absent_by_presence', { p_lesson: lessonId })),
    onSettled: () => client.invalidateQueries({ queryKey: ['journal'] }),
  });
}
